import React, { useCallback } from "react";
import { useQueryClient } from "react-query";
import { Link, useHistory } from "react-router-dom";
import { Cell, Column } from "react-table";
import { Button } from "@blueprintjs/core";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DataTable, fuzzyTextFilter } from "@components";
import { useBasePath, useOrg } from "@hooks";
import { Feature } from "@utils";

import { LastScanCell } from "@pages/projects/components/LastScanCell";
import { PolicyCell } from "@pages/projects/components/PolicyCell";
import { TagCell } from "@pages/projects/components/TagCell";
import { Repo } from "@pages/projects/types";

import { DeleteProjectsDialog } from "./DeleteProjectsDialog";

const PAGINATION_SETTINGS = { pageSize: 10 };

const COLUMNS: Column<Repo>[] = [
  {
    Header: "Project",
    id: "name",
    accessor: "name",
    Cell: ({ row }) => {
      let repo = row.original;
      let org = useOrg();
      return (
        <Link
          to={`/orgs/${org?.name || "-"}/findings?repo=${repo.name!}`}
          title="View Project Findings"
        >
          {row.original.name}
        </Link>
      );
    },
  },
  {
    Header: "Policies",
    id: "policies",
    accessor: (repo) => repo.policies.map((r) => r.name).join(","),
    Cell: ({ row }: Cell<Repo>) => (
      <PolicyCell selectedPolicies={row.original.policies} />
    ),
  },
  {
    Header: "Last scan",
    id: "last_scanned",
    accessor: (repo) =>
      repo.latest_scan_at && new Date(repo.latest_scan_at).valueOf(),
    Cell: ({ row }: Cell<Repo>) => <LastScanCell repo={row.original} />,
  },
  {
    Header: "",
    id: "findings",
    Cell: ({ row }: Cell<Repo>) => {
      let history = useHistory();
      const basePath = useBasePath();

      return (
        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() =>
              history.push(`${basePath}/projects/${row.original.name}`)
            }
            minimal
            title="View Project Settings"
          >
            <FontAwesomeIcon
              icon={faGear}
              style={{ color: "var(--link-color)" }}
            />
          </Button>
        </div>
      );
    },
  },
];

interface Props {
  repos: Repo[];
  rowSelect: boolean;
}

export const ProjectListPanel: React.FC<Props> = ({ repos, rowSelect }) => {
  const org = useOrg();
  const isNolicy = Feature.isNolicy({ org });
  const queryClient = useQueryClient();

  const refreshOnDelete = useCallback(() => {
    queryClient.invalidateQueries(["repos", { org }]);
  }, [queryClient, org]);

  if (org && !COLUMNS.find((c) => c.id === "tags")) {
    // insert tags column before settings and findings button
    COLUMNS.splice(3, 0, {
      Header: "Tags",
      id: "tags",
      accessor: (repo) => repo.tags?.join(","),
      Cell: ({ row }: Cell<Repo>) => <TagCell tags={row.original.tags || []} />,
    });
  }

  // We don't want to show the policies column for those orgs that are on the rule board
  // We filter here to just keep the order in place for everything else
  return (
    <div className="panel">
      <DataTable
        columns={
          isNolicy
            ? COLUMNS.filter((column) => column.id !== "policies")
            : COLUMNS
        }
        data={repos}
        pagination={PAGINATION_SETTINGS}
        filter={fuzzyTextFilter}
        rowSelect={
          rowSelect
            ? {
                Dialog: DeleteProjectsDialog,
                buttonText: "Delete",
                selectEnabled: true,
                refreshRows: refreshOnDelete,
              }
            : undefined
        }
        sorting
        disableRowHover
        colorLinks
      />
    </div>
  );
};
